import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { AppContext } from '../../../App';
import useGlynt from '../../hooks/useGlynt';

/**
 * Fetches the first page of existing MAT entries and extracts
 * all c-Field keys (keys starting with 'c-').
 * Used to show C-Fields in the creation pane for discovered entries.
 */
export default function useMatCFieldKeys() {
  const { orgId } = useContext(AppContext);
  const glynt = useGlynt();

  const { data: matPage } = useQuery({
    queryKey: ['mat-cfield-keys', orgId],

    queryFn: async () => {
      const g = await glynt;
      if (!orgId) throw new Error('Missing orgId');
      return g.mat.list(orgId, { limit: 50, offset: 0 });
    },

    enabled: !!orgId,
    staleTime: 10 * 60_000,
    gcTime: 15 * 60_000
  });

  const cFieldKeys = useMemo(() => {
    if (!matPage?.results?.length) return [];
    const keys = new Set<string>();
    matPage.results.forEach((row: Record<string, any>) => {
      Object.keys(row).forEach((k) => {
        if (k.startsWith('c-')) keys.add(k);
      });
    });
    return Array.from(keys).sort();
  }, [matPage]);

  return cFieldKeys;
}
